import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BsArrowRight } from 'react-icons/bs';

import { CoursesInterface } from 'types';
import { getCourseStatus, getResumeLesson } from 'utils';
import { useCourses } from 'contexts';

import './style.scss';

import noImage from 'assets/images/no-image.jpg';

export type CardCourseProps = {
  course: CoursesInterface;
  style?: React.CSSProperties;
  className?: string;
  [key: string]: any;
};

export function CardCourse({ course, style, className, ...rest }: CardCourseProps) {
  const { allProgress } = useCourses();

  const getPercent = useMemo(() => {
    let value = 0;

    if (allProgress?.length) {
      const coursePercent = allProgress.find(item => item._id === course._id);

      if (coursePercent) {
        value = coursePercent.percent;
      }
    }

    return value;
  }, [allProgress, course]);

  const status = useMemo(() => getCourseStatus(getPercent), [getPercent]);

  const statusColor = useMemo(() => {
    if (getPercent <= 0) return 'text-orange';
    if (getPercent > 0 && getPercent < 100) return 'text-green-500';
    return 'text-[#2bb788]';
  }, [getPercent]);

  const statusDotColor = useMemo(() => {
    if (getPercent <= 0) return 'bg-orange';
    if (getPercent > 0 && getPercent < 100) return 'bg-green-500';
    return 'bg-[#2bb788]';
  }, [getPercent]);

  const buttonLabel = useMemo(() => {
    if (getPercent <= 0) return 'Iniciar Curso';
    if (getPercent > 0 && getPercent < 100) return 'Continuar';
    return 'Ver Detalhes';
  }, [getPercent]);

  const buttonStyle = useMemo(() => {
    if (getPercent > 0 && getPercent < 100) {
      return 'bg-orange text-white';
    }
    return 'bg-white text-[#181b1a] border border-gray-200';
  }, [getPercent]);

  const courseProgress = useMemo(() => {
    return allProgress?.find(item => item._id === course._id) ?? null;
  }, [allProgress, course._id]);

  const resumeLink = useMemo(() => {
    if (getPercent >= 100) {
      return `/cursos/${course._id}`;
    }

    if (courseProgress) {
      const resume = getResumeLesson(courseProgress);
      if (resume) return `/cursos/${course._id}/${resume.folderID}/${resume.contentID}`;
    }

    return `/cursos/${course._id}`;
  }, [getPercent, courseProgress, course._id]);

  return (
    <div className={`${className || ''}`} style={style} {...rest}>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5 h-full flex flex-col">
        {/* Imagem + info — sempre vai para detalhes/trilhas */}
        <Link to={`/cursos/${course._id}`} className="flex items-start gap-4 mb-4 flex-1">
          <div className="w-[100px] h-[90px] lg:w-[180px] lg:h-[160px] rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
            <img
              src={course.extra?.image?.length ? course.extra?.image : noImage}
              alt={course.title || ' '}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex-1 min-w-0">
            {!!status.length && (
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className={`w-2 h-2 rounded-full ${statusDotColor}`} />
                <span className={`text-xs font-bold uppercase ${statusColor}`}>{status}</span>
              </div>
            )}

            <h3 className="text-base lg:text-lg font-semibold text-[#181b1a] leading-tight mb-1 line-clamp-2">
              {course.title || ' '}
            </h3>

            {!!course.extra?.description && (
              <p
                className="text-xs lg:text-sm text-gray-500 leading-snug line-clamp-2"
                dangerouslySetInnerHTML={{ __html: course.extra.description }}
              />
            )}
            <Link to={resumeLink} className="block mt-2 mb-4">
          <span
            className={`flex justify-center items-center gap-2 px-5 py-2.5 mt-5 rounded-md text-lg font-semibold ${buttonStyle}`}
          >
            {buttonLabel} <BsArrowRight className="text-base" />
          </span>
        </Link>
          </div>
        </Link>


        {/* Barra de progresso */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Progresso</span>
            {getPercent > 0 ? (
              <span className="text-sm text-orange font-semibold">{`${Math.round(getPercent)}% concluído`}</span>
            ) : (
              <span className="text-sm text-gray-400 italic">Não iniciado</span>
            )}
          </div>
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-orange transition-all duration-500"
              style={{ width: `${Math.round(getPercent)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
